import { FC, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import MenuIcon from '@mui/icons-material/Menu'
import { AppBar as MUIAppBar, Grid, IconButton, List, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

import { Profile } from '@components/AppBar/components/Profile'
import { AppLoader } from '@components/AppLoader'
import { UserNavList } from '@components/UserNavList'

import { useFetchParameterPropertiesQuery } from '@redux/api/parameters.api'

import { transformCommonParametersToValues } from '@helpers'
import { COMMON_PARAMETERS, DRAWER_WIDTH, ROUTES } from '@constants'
import { TransformLinesForAppBarType } from '@types'

type AppBarProps = {
  open: boolean
  options: TransformLinesForAppBarType
  loadingOptions: boolean
  isAdmin: boolean
  setOpen?: (sidebarOpen: boolean) => void
}

const StyledAppBar = styled(MUIAppBar, {
  shouldForwardProp: prop => prop !== 'open',
})<Omit<AppBarProps, 'setOpen' | 'options' | 'loadingOptions'>>(({ theme, open, isAdmin }) => ({
  zIndex: theme.zIndex.drawer + 1,
  '& .MuiToolbar-root': {
    paddingLeft: theme.spacing(2),
  },
  padding: '0 !important',
  alignItems: 'center',
  boxShadow: 'none',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open &&
    isAdmin && {
      marginLeft: DRAWER_WIDTH,
      width: `calc(100% - ${DRAWER_WIDTH}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
}))

export const AppBar: FC<AppBarProps> = ({ options, loadingOptions, open, setOpen, isAdmin }) => {
  const navigate = useNavigate()

  const { data: commonParameters } = useFetchParameterPropertiesQuery({
    code: COMMON_PARAMETERS.STATE,
  })

  const initialCommonParameters = useMemo(
    () => transformCommonParametersToValues(commonParameters),
    [commonParameters]
  )

  const handleClick = () => {
    setOpen?.(true)
  }

  const handleNavigateToDefaultPage = () => {
    navigate(ROUTES.DEFAULT_PAGE)
  }

  return (
    <StyledAppBar isAdmin={isAdmin} open={open} position='static'>
      <Grid
        container
        alignItems={'center'}
        bgcolor={theme => theme.palette.primary.main}
        flexWrap={'nowrap'}
        py={'4px'}
      >
        <Grid container item alignItems={'center'}>
          <Grid item>
            {!open && (
              <IconButton
                sx={{
                  // mr: 3,
                  color: theme => theme.palette.background.paper,
                  '& .MuiTouchRipple-child': { borderRadius: 'inherit' },
                  borderRadius: 0,
                }}
                onClick={handleClick}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Grid>
          {/*<Grid*/}
          {/*  item*/}
          {/*  sx={{*/}
          {/*    ml: '19px',*/}
          {/*    mr: '12px',*/}
          {/*    alignItems: 'center',*/}
          {/*  }}*/}
          {/*>*/}
          {/*  <Button*/}
          {/*    sx={{*/}
          {/*      padding: '0 8px',*/}
          {/*      minWidth: 0,*/}
          {/*      ':hover': {*/}
          {/*        background: '#8c8c8c',*/}
          {/*      },*/}
          {/*    }}*/}
          {/*    onClick={handleNavigateToDefaultPage}*/}
          {/*  >*/}
          {/*    <CustomSvgIcon src={logo} sx={{ width: 24, height: 26 }} />*/}
          {/*  </Button>*/}
          {/*</Grid>*/}
          <Grid item>
            <Typography
              color={'white'}
              variant={'h5'}
              sx={{
                ml: 1.5,
                cursor: 'pointer',

                // fontWeight: 400, lineHeight: '32px', fontSize: 18
              }}
              onClick={handleNavigateToDefaultPage}
            >
              {initialCommonParameters.appTitle}
            </Typography>
          </Grid>
        </Grid>
        <Grid item justifySelf={'flex-end'} mr={'27px'}>
          <Profile />
        </Grid>
      </Grid>

      {loadingOptions ? (
        <List
          key='loadingApp'
          sx={{
            display: 'flex',
            pt: 1,
            pb: 1,
            bgcolor: theme => theme.palette.background.topMenu,
            width: '100%',
            overflowX: 'auto',
            alignItems: 'center',
          }}
        >
          <AppLoader size='2rem' />
        </List>
      ) : (
        options.map((line, index) => (
          <List
            key={line.id}
            sx={{
              display: 'flex',
              pt: index === 0 ? 1 : 0,
              pb: 0.7,
              // px: 1,
              bgcolor: theme => theme.palette.background.topMenu,
              width: '100%',
              overflowX: 'auto',
              alignItems: 'center',
            }}
          >
            {line.items.map(option => (
              <UserNavList key={option.id} itemsLine={line.items} option={option} />
            ))}
          </List>
        ))
      )}
    </StyledAppBar>
  )
}
